import styled from '@emotion/styled'
import React, { useContext, useState } from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { CartContext } from '../../../context/cartContext'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { STICKY_FOOTER_SHADOW } from '../../../style/styleConstants'
import { priceFormat } from '../../../utils/priceFormat'
import { calculateDiscountPercentage } from '../../../utils/discounts'
import { CartShippingLine, getCartSummary } from '../../../utils/cartSummary'
import { FormattedPriceDisplay } from '../../shared/PriceDisplay'
import { LockIcon, PriceTagIcon } from '../../icons/Icons'
import { ChevronDownIcon, ChevronUpIcon } from '../../icons/Chevron'
import { CTAButton } from '../../shared/CTAButton'
import { ShopPayCTAButton } from '../../shared/ShopPayCTAButton'
import { PaymentMethods } from './PaymentMethods'
import { DiscountErrorBoundary } from '../../shared/DiscountErrorBoundary'
import {
    CustomizationContext,
    PRIMARY_DISCOUNT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
    SECONDARY_DISCOUNT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import { useBenefitsCarousel } from '../../../hooks/useBenefitsCarousel'
import { useCartDiscounts } from '../../../hooks/useCartDiscounts'
import { useShopPayButtons } from '../../../hooks/useShopPayButtons'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import { hoverTintSurface } from '../../../style/interactiveStates'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'
import {
    BenefitsCarouselViewport,
    BenefitSlide,
    BenefitSlideContent,
} from '../../shared/BenefitsCarousel'
import { scaledFontSize } from '../../../style/scrollingLayout'

// Styled components
const FooterContainer = styled.div<{
    zIndex?: number | string
    $hasCheckoutBenefits?: boolean
}>(({ zIndex, $hasCheckoutBenefits }) => ({
    position: 'sticky',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    paddingBottom: $hasCheckoutBenefits ? '2px' : '10px',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    borderTop: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
    fontSize: scaledFontSize(14),
    zIndex: zIndex || 10,
    boxShadow: STICKY_FOOTER_SHADOW,
    maxWidth: '100%',
}))

const FooterInnerContainer = styled.div({
    padding: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
})

const CompactTotalContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    // 12px vertical, 16px horizontal padding
    padding: '10px 8px 0 16px',
    ...getTypographyRoleStyle('featureTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 700,
    }),
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
    cursor: 'pointer',
    '&:hover': {
        opacity: 0.8,
    },
})

const TotalDiscountBadge = styled.span({
    backgroundColor: `var(${PRIMARY_DISCOUNT_COLOR_VAR})`,
    color: `var(${SECONDARY_DISCOUNT_COLOR_VAR})`,
    ...getTypographyRoleStyle('badgeText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
    padding: '2px 6px',
    borderRadius: '6px',
    marginRight: '8px',
})

const StrikethroughPrice = styled.span({
    textDecoration: 'line-through',
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    ...getTypographyRoleStyle('compareAtPrice', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(13),
})

const ListPriceText = styled.span({
    ...getTypographyRoleStyle('captionText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(13),
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    marginTop: '4px',
})

const PriceAndChevronWrapper = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
})

const PriceContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
})

const AccordionContent = styled.div<{ isOpen: boolean }>(({ isOpen }) => ({
    width: '100%',
    overflow: 'hidden',
    transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
    maxHeight: isOpen ? '200px' : '0px',
    opacity: isOpen ? 1 : 0,
}))

const PriceBreakdownContainer = styled.div({
    padding: '16px 16px 0px 16px',
})

const OrderSummaryBorder = styled.div({
    borderBottom: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
    marginTop: '16px',
})

const OrderSummaryHeader = styled.div({
    ...getTypographyRoleStyle('featureTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 700,
    }),
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    marginBottom: '8px',
})

const DiscountCodeBadge = styled.span({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#f0f0f0',
    padding: '3px 7px',
    borderRadius: '4px',
    ...getTypographyRoleStyle('badgeText'),
})

const DiscountRowContent = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
})

const PriceRow = styled.div<{ isDiscount?: boolean }>(
    ({ isDiscount }: { isDiscount?: boolean }) => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 2,
        ...getTypographyRoleStyle('bodyText', {
            fontWeight: 400,
        }),
        fontSize: scaledFontSize(14),
        color: isDiscount
            ? `var(${PRIMARY_DISCOUNT_COLOR_VAR})`
            : `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        '&:last-child': {
            marginBottom: 0,
        },
    }),
)

const ChevronContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '24px', // Match the height of the price line
})

const TotalLabelContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
})

const TotalValueContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
})

const CheckoutButtonContainer = styled.div({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 8,
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    paddingTop: '8px',
})

const SecureCheckoutContent = styled.div({
    display: 'flex',
    alignItems: 'center',
})

/** The whole row of payment marks starts checkout, so it reads as a control. */
const PaymentMethodsContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    ...hoverTintSurface(),
})

const ShippingValue: React.FC<{
    shipping: CartShippingLine
    currencySymbol?: string | null
}> = ({ shipping, currencySymbol }) => {
    switch (shipping.kind) {
        case 'charged':
            return <>{priceFormat(shipping.cost, currencySymbol)}</>
        case 'free':
            return (
                <>
                    {shipping.strikethroughCost !== null && (
                        <>
                            <StrikethroughPrice>
                                {priceFormat(
                                    shipping.strikethroughCost,
                                    currencySymbol,
                                )}
                            </StrikethroughPrice>{' '}
                        </>
                    )}
                    FREE
                </>
            )
        case 'calculatedAtCheckout':
            return <>Calculated at checkout</>
    }
}

interface CartFooterProps {
    zIndex?: number | string
}

export const CartFooter: React.FC<CartFooterProps> = ({ zIndex = 10 }) => {
    const { landingPage, discountCode } = useContext(ViddyLPContext)
    const { resolvedLandingPageLinks } = useContext(LandingPageLinksContext)
    const checkoutButtonBenefits = landingPage.checkoutButtonBrandBenefits
    const checkoutBenefitsCarouselIndex = useBenefitsCarousel(
        checkoutButtonBenefits,
    )
    const {
        primaryCtaTextColor,
        freeShippingEnabled,
        freeShippingMinCartAmount,
        freeShippingTypicalCost,
        freeShippingCostInSubtotal,
        autoExpandCart,
    } = useContext(CustomizationContext)
    const [isAccordionOpen, setIsAccordionOpen] = useState(autoExpandCart)
    const displayShopPayButtons = useShopPayButtons()
    const { Analytics } = useContext(AnalyticsContext)
    const { cart, onCheckout, getCartSubtotal, getCartTotal } =
        useContext(CartContext)

    const {
        saleSavings,
        discountCodeSavings,
        totalItemCount,
        hasSaleSavings,
        hasDiscountCode,
        hasAnySavings,
    } = useCartDiscounts(cart, resolvedLandingPageLinks, landingPage.discount)

    const _onCheckout = () => {
        Analytics.TRACK_CART_CHECKOUT_CLICK({ cart })
        onCheckout()
    }

    const _onShopPayCheckout = () => {
        Analytics.TRACK_CART_CHECKOUT_CLICK({ cart })
        onCheckout(true)
    }

    if (!cart.items.length) {
        return null
    }

    // getCartSubtotal is the undiscounted items price, getCartTotal the discounted one
    const originalPrice = getCartSubtotal()
    const { shipping, listPrice, subtotal, hasShippingSavings } =
        getCartSummary({
            itemsListPrice: originalPrice,
            itemsTotal: getCartTotal(),
            freeShippingEnabled,
            freeShippingMinCartAmount,
            freeShippingTypicalCost,
            freeShippingCostInSubtotal,
        })
    const showSavings = hasAnySavings || hasShippingSavings
    const currencySymbol = landingPage.client?.currencySymbol

    const toggleAccordion = () => {
        setIsAccordionOpen(!isAccordionOpen)
    }

    return (
        <FooterContainer
            zIndex={zIndex}
            $hasCheckoutBenefits={checkoutButtonBenefits.length > 0}
        >
            <AccordionContent isOpen={isAccordionOpen}>
                <PriceBreakdownContainer>
                    <OrderSummaryHeader>Order Summary</OrderSummaryHeader>

                    <PriceRow>
                        <span>
                            {totalItemCount === 1 ? 'Item' : 'Items'} (
                            {totalItemCount}):
                        </span>
                        <span>
                            {priceFormat(originalPrice, currencySymbol)}
                        </span>
                    </PriceRow>

                    <DiscountErrorBoundary>
                        {hasSaleSavings && (
                            <PriceRow>
                                <span>Exclusive sale savings:</span>
                                <span>
                                    -{priceFormat(saleSavings, currencySymbol)}
                                </span>
                            </PriceRow>
                        )}

                        {hasDiscountCode && (
                            <PriceRow>
                                <DiscountRowContent>
                                    <span>Exclusive discount savings:</span>
                                    <DiscountCodeBadge>
                                        <PriceTagIcon
                                            width={14}
                                            height={14}
                                            color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                                        />
                                        <span>
                                            {cart.discountCodes
                                                ?.filter(
                                                    (dc) =>
                                                        dc.applicable &&
                                                        dc.code,
                                                )
                                                .map((dc) => dc.code)
                                                .join(', ') ||
                                                discountCode ||
                                                'Discount'}
                                        </span>
                                    </DiscountCodeBadge>
                                </DiscountRowContent>
                                <span>
                                    -
                                    {priceFormat(
                                        discountCodeSavings,
                                        currencySymbol,
                                    )}
                                </span>
                            </PriceRow>
                        )}
                    </DiscountErrorBoundary>

                    <PriceRow>
                        <span>Shipping:</span>
                        <span>
                            <ShippingValue
                                shipping={shipping}
                                currencySymbol={currencySymbol}
                            />
                        </span>
                    </PriceRow>
                    <OrderSummaryBorder />
                </PriceBreakdownContainer>
            </AccordionContent>

            <CompactTotalContainer onClick={toggleAccordion}>
                <TotalLabelContainer>
                    <span>
                        Subtotal ({totalItemCount}{' '}
                        {totalItemCount === 1 ? 'item' : 'items'})
                    </span>
                </TotalLabelContainer>
                <PriceAndChevronWrapper>
                    <TotalValueContainer>
                        <PriceContainer>
                            <DiscountErrorBoundary>
                                {showSavings && (
                                    <TotalDiscountBadge>
                                        -
                                        {calculateDiscountPercentage(
                                            listPrice,
                                            subtotal,
                                        )}
                                        %
                                    </TotalDiscountBadge>
                                )}
                            </DiscountErrorBoundary>
                            <FormattedPriceDisplay
                                priceInCents={subtotal}
                                currencySymbol={currencySymbol}
                                dollarsSize={20}
                                centsSize={14}
                                topOffset="-0.32em"
                            />
                        </PriceContainer>
                        {showSavings && (
                            <ListPriceText>
                                List Price:{' '}
                                <StrikethroughPrice>
                                    {priceFormat(listPrice, currencySymbol)}
                                </StrikethroughPrice>
                            </ListPriceText>
                        )}
                    </TotalValueContainer>
                    <ChevronContainer>
                        {isAccordionOpen ? (
                            <ChevronDownIcon />
                        ) : (
                            <ChevronUpIcon />
                        )}
                    </ChevronContainer>
                </PriceAndChevronWrapper>
            </CompactTotalContainer>

            <FooterInnerContainer>
                <CheckoutButtonContainer>
                    {displayShopPayButtons ? (
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 12,
                                paddingLeft: 16,
                                paddingRight: 16,
                                boxSizing: 'border-box',
                            }}
                        >
                            <CTAButton
                                buttonText={
                                    <SecureCheckoutContent>
                                        <LockIcon color={primaryCtaTextColor} />
                                        Secure Checkout
                                    </SecureCheckoutContent>
                                }
                                fitLabel="Secure Checkout"
                                onClick={_onCheckout}
                                width="100%"
                                height={48}
                                loader={true}
                            />
                            <ShopPayCTAButton
                                fullWidth
                                onClick={_onShopPayCheckout}
                                loader={true}
                                variant="checkout"
                            />
                        </div>
                    ) : (
                        <CTAButton
                            buttonText={
                                <SecureCheckoutContent>
                                    <LockIcon color={primaryCtaTextColor} />
                                    Secure Checkout
                                </SecureCheckoutContent>
                            }
                            fitLabel="Secure Checkout"
                            onClick={_onCheckout}
                            width={'90%'}
                            height={48}
                            loader={true}
                        />
                    )}
                </CheckoutButtonContainer>

                <PaymentMethodsContainer onClick={_onCheckout}>
                    <PaymentMethods />
                </PaymentMethodsContainer>
                {checkoutButtonBenefits.length > 0 && (
                    <BenefitsCarouselViewport paddingTop="5px" minHeight={40}>
                        {/* Auto-rotating carousel: opacity transition with 0.3s delay for fade-in,
                            aria-hidden for accessibility. Secure Checkout uses text-only (no icon)
                            and fontWeight 100 per design. */}
                        {checkoutButtonBenefits.map((benefit, index) => (
                            <BenefitSlide
                                key={benefit.id}
                                isActive={
                                    index === checkoutBenefitsCarouselIndex
                                }
                                delay={
                                    index === checkoutBenefitsCarouselIndex
                                        ? 0.3
                                        : 0
                                }
                                fontWeight={100}
                                gap={0}
                                $fillBackgroundWhenActive={true}
                                aria-hidden={
                                    index !== checkoutBenefitsCarouselIndex
                                }
                            >
                                <BenefitSlideContent
                                    benefit={benefit}
                                    showIcon={false}
                                />
                            </BenefitSlide>
                        ))}
                    </BenefitsCarouselViewport>
                )}
            </FooterInnerContainer>
        </FooterContainer>
    )
}
